<template>
  <div class="sharing-settings-by-partners-tab">
    <div
      class="relative min-h-[50vh]"
      :class="{
        'sharing-settings-by-partners-tab--loading': !ready,
      }"
    >
      <BittsTablePro
        :columns="columnDefinitions"
        :default-column-def="defaultColumnDefinition"
        :rows="rows"
        :row-height="52"
        row-id="id"
        :pagination="false"
        :suppress-movable-columns="true"
        :no-rows="!rows.length && ready"
        :no-rows-props="{
          title: 'No partners found',
          description:
            'You don\'t have any active partners yet. Invite partners to start sharing data.',
          icon: ['fak', 'partner'],
        }"
        :passed-context="gridContext"
        :row-selection="rowSelection"
      />
      <div v-if="!ready" class="sharing-settings-by-partners-tab__loading">
        <BittsLoading />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsLoading, BittsTablePro } from '@crossbeam/bitts';

import type { RowSelectionOptions } from '@ag-grid-community/core';
import { computed, ref } from 'vue';

import useDataSharingPermissions from '@/composables/useDataSharingPermissions';
import { useTeamStore } from '@/stores';
import { useDataSharesStore } from '@/stores/DataSharesStore';
import { usePartnersStore } from '@/stores/PartnersStore';
import { usePopulationsStore } from '@/stores/PopulationsStore';
import { buildOwnerOptions } from '@/utils/ownerOptions';

import {
  defaultColumnDefinition,
  usePartnerTabColumnDefinitions,
} from './composables/usePartnerTabColumnDefinitions';
import { buildPartnerRows } from './partnerRows';

const partnersStore = usePartnersStore();
const populationsStore = usePopulationsStore();
const dataSharesStore = useDataSharesStore();
const teamStore = useTeamStore();
const { canEdit } = useDataSharingPermissions();

const columnDefinitions = usePartnerTabColumnDefinitions();

const ready = computed(
  () => partnersStore.ready && populationsStore.ready && dataSharesStore.ready,
);

const rows = computed(() => {
  if (!ready.value) return [];

  return buildPartnerRows(partnersStore.partnerOrgs, {
    populations: populationsStore.populations,
    partnerPopulations: populationsStore.partnerPopulations,
    greenfieldSharesLookup: dataSharesStore.greenfieldSharesLookup,
    getOutgoingVisibility: (populationId, partnerOrgId) =>
      dataSharesStore.getEffectiveVisibilityForPopulationWithPartner(
        populationId,
        partnerOrgId,
      ),
    getIncomingSharedFieldCount: (partnerPopulationId) =>
      dataSharesStore.getIncomingSharingRules({
        partnerPopulationId,
        isOverride: false,
      })[0]?.shared_fields.length ?? 0,
  });
});

/**
 * Owner options are identical for every row, so they are built once here rather
 * than per cell instance.
 */
const teamOwnerOptions = computed(() => buildOwnerOptions(teamStore.coreUsers));

const gridContext = {
  canSelectRows: () => canEdit.value,
  teamOwnerOptions: () => teamOwnerOptions.value,
};

const rowSelection = computed<RowSelectionOptions | undefined>(() =>
  canEdit.value
    ? {
        mode: 'multiRow',
        enableClickSelection: false,
        checkboxes: false,
        headerCheckbox: false,
      }
    : undefined,
);

const hasAnyChanges = ref(false);
const isSaving = ref(false);

defineExpose({
  hasAnyChanges,
  isSaving,
  async saveChanges() {
    // No-op: By Partners tab has no editable state yet
  },
});
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.sharing-settings-by-partners-tab {
  @apply flex flex-col w-full;
}
.sharing-settings-by-partners-tab--loading {
  @apply opacity-50 pointer-events-none;
}
.sharing-settings-by-partners-tab__loading {
  @apply absolute inset-0 flex items-center justify-center;
}

/* --- Edge-to-edge table (like PartnerTrackerPage) --- */
:deep(.ag-root-wrapper) {
  @apply rounded-none! border-x-0 border-b-0;
}

/* --- AG Grid column group borders (mirroring PartnerLeaderboardTable) --- */
:deep(.ag-header-cell) {
  @apply border-0!;
}
:deep(.ag-header-group-cell:not(.ag-header-group-cell-no-group)) {
  @apply border-0! border-r! border-solid! border-neutral-300;
}
:deep(.ag-pinned-left-header .ag-header-group-cell-no-group) {
  @apply border-0! border-r! border-solid! border-neutral-300;
}
:deep(.border-end) {
  @apply border-0! border-r! border-solid! border-neutral-300;
}
:deep(.ag-header-row.ag-header-row-column-group) {
  @apply bg-neutral-background text-xs text-neutral-text-strong font-normal;
}

:deep(.ag-header-group-cell-label) {
  @apply justify-center;
}

/* Segment columns centre their header label and value, matching the design */
:deep(.segment-header .ag-header-cell-label) {
  @apply justify-center;
}

:deep(.ag-header-group-cell.ag-header-group-cell-no-group) {
  @apply bg-neutral-background;
}

:deep(.ag-cell-wrapper) {
  @apply h-full min-w-0;
}

:deep(.ag-row-selected::before) {
  @apply bg-transparent;
}

:deep(.ag-overlay-no-rows-wrapper .c-bitts-empty-state-large-border) {
  @apply border-none;
}
</style>
